import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../Components/common/Navbar";
import Footer from "../../Components/common/Footer";
import {
  faGraduationCap,
  faAward,
  faUsers,
  faRocket,
  faHeart,
  faHandshake,
  faEye,
  faBullseye,
  faArrowRight,
  faCheckDouble,
  faStar,
  faBook,
  faTrophy,
  faQuoteLeft,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function AnimatedCounter({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView(0.2);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return <span ref={ref}>{count.toLocaleString()}</span>;
}

export default function AboutUs() {
  const navigate = useNavigate();
  const [heroVisible, setHeroVisible] = useState(false);
  const [storyRef, storyInView] = useInView();
  const [valuesRef, valuesInView] = useInView();
  const [teamRef, teamInView] = useInView();
  const [statsRef, statsInView] = useInView();

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const values = [
    { icon: faHeart, title: "Passion for Learning", desc: "We believe education is the most powerful tool for changing the world. Every decision we make starts with the learner.", gradient: "from-rose-500 to-pink-600" },
    { icon: faHandshake, title: "Integrity First", desc: "We build trust through transparency, honest communication, and always doing what's right — even when it's hard.", gradient: "from-primary to-primary-dark" },
    { icon: faRocket, title: "Constant Innovation", desc: "We never stop improving. From course content to platform features, we push boundaries to deliver better outcomes.", gradient: "from-secondary to-secondary-dark" },
    { icon: faEye, title: "Inclusive Vision", desc: "Quality education for everyone, everywhere. We work to remove barriers and make learning accessible to all.", gradient: "from-purple-500 to-indigo-600" },
    { icon: faBullseye, title: "Results Driven", desc: "We measure success by your success. Our courses are designed to deliver real-world skills and career outcomes.", gradient: "from-warning to-yellow-500" },
    { icon: faCheckDouble, title: "Quality Assured", desc: "Every course is reviewed, updated, and held to the highest standards so you always get accurate, valuable content.", gradient: "from-teal-500 to-primary" },
  ];

  const team = [
    { name: "Dr. Aisha Rahman", role: "Founder & CEO", bio: "Former Stanford professor with 15 years in EdTech. Passionate about democratizing education globally.", initials: "AR", from: "from-primary", to: "to-teal-400" },
    { name: "Marcus Lee", role: "Head of Curriculum", bio: "Ex-Google engineer turned educator. Designed learning paths for 50,000+ students across 80 countries.", initials: "ML", from: "from-secondary", to: "to-orange-400" },
    { name: "Priya Sharma", role: "Chief Technology Officer", bio: "Full-stack architect who built Alkauser Academy from the ground up. Loves clean code and great user experiences.", initials: "PS", from: "from-purple-500", to: "to-indigo-500" },
  ];

  const stats = [
    { icon: faUsers, value: 10000, label: "Students Worldwide", suffix: "+" },
    { icon: faBook, value: 100, label: "Expert Courses", suffix: "+" },
    { icon: faAward, value: 95, label: "Completion Rate", suffix: "%" },
    { icon: faTrophy, value: 50, label: "Industry Awards", suffix: "+" },
  ];

  const milestones = [
    { year: "2019", title: "Alkauser Academy  Founded", desc: "Started with 5 courses and a dream to make quality education accessible to everyone." },
    { year: "2020", title: "10,000 Students", desc: "Crossed our first major milestone during a year when online learning became essential." },
    { year: "2022", title: "Global Expansion", desc: "Launched in 40+ countries and partnered with top universities and tech companies." },
    { year: "2024", title: "100+ Courses Live", desc: "Expanded our catalog to cover web dev, data science, business, and design." },
  ];

  return (
    <div className="bg-gray-50 min-h-screen overflow-x-hidden">
      <Navbar page="about" />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-28 flex items-center justify-center bg-gradient-to-br from-[#0a2e2d] via-primary-dark to-primary text-center">
        {/* dot grid */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        {/* decorative rings */}
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full border-[36px] border-secondary/20 pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-60 h-60 rounded-full border-[24px] border-warning/15 pointer-events-none" />
        {/* blobs */}
        <div className="absolute top-10 left-10 w-48 h-48 bg-secondary/15 rounded-full blur-3xl animate-float pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-56 h-56 bg-warning/10 rounded-full blur-3xl animate-float-slow pointer-events-none" />

        <div className={`relative z-10 px-6 max-w-3xl mx-auto transition-all duration-1000 ease-out ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-white/85 text-sm font-medium mb-6">
            <FontAwesomeIcon icon={faHeart} className="text-rose-400 text-xs" />
            Our Story
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-5">
            We're on a Mission to
            <span className="block mt-2 bg-gradient-to-r from-warning via-yellow-300 to-secondary bg-clip-text text-transparent">
              Change Education
            </span>
          </h1>
          <p className="text-white/75 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Alkauser Academy  was built on a simple belief — every person deserves access to world-class education, regardless of where they come from.
          </p>
        </div>
      </section>

      {/* ── Our Story ── */}
      <section className="px-6 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            ref={storyRef}
            className={`grid md:grid-cols-2 gap-14 items-center transition-all duration-700 ${storyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {/* Left: text */}
            <div>
              <span className="text-secondary font-semibold text-sm uppercase tracking-widest">Our Journey</span>
              <h2 className="text-4xl font-extrabold text-gray-900 mt-2 mb-6 leading-tight">
                From a Small Idea <br /> to a Global Platform
              </h2>
              <div className="space-y-4 text-gray-500 leading-relaxed">
                <p>
                  Alkauser Academy started in 2019 with a single laptop and an ambitious idea: create an online learning platform
                  that actually gets people hired. Not just videos — but structured, practical, career-focused education.
                </p>
                <p>
                  Within a year, our first 10,000 students had enrolled. Today, learners from over 40 countries trust
                  Alkauser Academy to power their careers in technology, business, and design.
                </p>
                <p>
                  We partner with industry experts, practicing engineers, and educators to craft content that reflects
                  real-world demands — not just textbook theory.
                </p>
              </div>
              <button
                onClick={() => navigate("/courses")}
                className="mt-8 group flex items-center gap-2 bg-primary text-white px-7 py-3 rounded-full font-bold hover:bg-primary-dark transition-all duration-300 hover:scale-105 shadow-md shadow-primary/20"
              >
                Explore Our Courses
                <FontAwesomeIcon icon={faArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Right: milestone timeline */}
            <div className="space-y-6">
              {milestones.map((m, i) => (
                <div
                  key={i}
                  className={`flex gap-5 transition-all duration-500 ${storyInView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
                  style={{ transitionDelay: `${i * 150}ms` }}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-primary text-white font-extrabold text-sm flex items-center justify-center shrink-0 shadow-md shadow-primary/30">
                      {m.year.slice(2)}
                    </div>
                    {i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-primary/20 mt-2" />}
                  </div>
                  <div className="pb-6">
                    <span className="text-xs text-primary font-bold uppercase tracking-widest">{m.year}</span>
                    <h4 className="text-gray-900 font-bold text-lg mt-0.5 mb-1">{m.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="bg-gradient-to-br from-[#0a2e2d] via-primary-dark to-primary py-16 px-6">
        <div ref={statsRef} className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`transition-all duration-700 ${statsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                <FontAwesomeIcon icon={stat.icon} className="text-warning text-xl" />
              </div>
              <p className="text-3xl font-extrabold text-white">
                <AnimatedCounter target={stat.value} />{stat.suffix}
              </p>
              <p className="text-white/65 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Values ── */}
      <section className="px-6 py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">What Drives Us</span>
            <h2 className="text-4xl font-extrabold text-gray-800 mt-2">Our Core Values</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">The principles that guide every course, every feature, and every decision we make.</p>
          </div>
          <div ref={valuesRef} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {values.map((v, i) => (
              <div
                key={i}
                className={`group p-7 rounded-2xl bg-white shadow-sm hover:shadow-xl border border-gray-100 hover:border-primary/20 transition-all duration-500 hover:-translate-y-2 ${valuesInView ? "animate-fadeInUp" : "opacity-0"}`}
                style={{ animationDelay: `${i * 100}ms`, animationFillMode: "both" }}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${v.gradient} flex items-center justify-center mb-5 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  <FontAwesomeIcon icon={v.icon} className="text-white text-xl" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg mb-2 group-hover:text-primary transition-colors duration-300">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Team ── */}
      <section className="px-6 py-20 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-secondary font-semibold text-sm uppercase tracking-widest">The People</span>
            <h2 className="text-4xl font-extrabold text-gray-800 mt-2">Meet Our Team</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">Educators, engineers, and dreamers working together to build the future of online learning.</p>
          </div>
          <div ref={teamRef} className="grid md:grid-cols-3 gap-8">
            {team.map((member, i) => (
              <div
                key={i}
                className={`group text-center p-8 rounded-2xl bg-gray-50 hover:bg-white border border-gray-100 hover:border-primary/20 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${teamInView ? "animate-fadeInUp" : "opacity-0"}`}
                style={{ animationDelay: `${i * 150}ms`, animationFillMode: "both" }}
              >
                {/* Avatar */}
                <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${member.from} ${member.to} flex items-center justify-center mx-auto mb-5 text-white text-2xl font-extrabold shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {member.initials}
                </div>
                {/* Stars */}
                <div className="flex justify-center gap-0.5 mb-3">
                  {[...Array(5)].map((_, s) => <FontAwesomeIcon key={s} icon={faStar} className="text-warning text-xs" />)}
                </div>
                <h3 className="font-extrabold text-gray-900 text-lg mb-1 group-hover:text-primary transition-colors duration-300">{member.name}</h3>
                <p className="text-primary text-sm font-semibold mb-3">{member.role}</p>
                <p className="text-gray-500 text-sm leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonial quote ── */}
      <section className="px-6 py-16" style={{ backgroundColor: "#f5f0e8" }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-6">
            <FontAwesomeIcon icon={faQuoteLeft} className="text-primary text-xl" />
          </div>
          <blockquote className="text-2xl md:text-3xl font-bold text-gray-800 leading-snug mb-6">
            "Education is not the filling of a pail, but the lighting of a fire."
          </blockquote>
          <p className="text-gray-500 font-medium">— W.B. Yeats &nbsp;·&nbsp; <span className="text-primary">EduVerse's guiding philosophy</span></p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative px-6 py-20 overflow-hidden bg-gradient-to-br from-primary-dark via-primary to-secondary/80 text-white text-center">
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-warning/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="text-4xl font-extrabold mb-4">Ready to Start Learning?</h2>
          <p className="text-white/80 text-lg mb-8">Join 10,000+ learners and start building skills that actually matter.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/courses")}
              className="group px-8 py-4 rounded-2xl bg-warning text-black font-bold hover:bg-yellow-300 transition-all duration-300 hover:scale-105 shadow-lg shadow-warning/30 flex items-center justify-center gap-2"
            >
              Browse Courses
              <FontAwesomeIcon icon={faArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="px-8 py-4 rounded-2xl bg-white/10 border border-white/30 text-white font-bold hover:bg-white/20 transition-all duration-300 hover:scale-105"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
